/* global Office */
import { CoRetrieveDocumentId } from "../../components/generic/CoRetrieveDocumentId";
import React, { useEffect, useState } from "react";
import { DocumentNotValidError } from "../../components/generic/DocumentNotValidError";
import { AllCoObjectsSelector } from "../../components/generic/AllCoObjectsSelector";
import { ObjectsListUpdatesPanel } from "../../components/document/ObjectsListUpdatesPanel";
import { CoObjectItem } from "../../components/interfaces/CoObject";
import { launchRefreshElaboration } from "../../utils/helpers/ElaborationHelper";
import { IconButton, IContextualMenuItem, PrimaryButton, Stack, TeachingBubble } from "@fluentui/react";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { FullPageModal } from "../../components/layout/FullPageModal";
import { Toggle } from "@fluentui/react/lib/Toggle";
import FooterPanel from "../../components/layout/FooterPanel";
import { UpdateOptions } from "../../api/object/RefreshObject";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import { ProtectedComponent } from "../../components/wrappers/ProtectedComponent";
import { UnauthorizedError } from "../../components/generic/UnauthorizedError";
import { TableUpdateStrategy } from "../../components/coobjects/co-panel/ObjectPanelComponent";
import { NotCompatibleWithWeb } from "../../components/layout/ConfirmationModal";

export const UpdatesPage: React.FunctionComponent = () => {
  if (Office.context.requirements.isSetSupported("WordApiOnline", "1.1")) return <NotCompatibleWithWeb />;

  return (
    <ProtectedComponent protectionType={true} errorComponent={<UnauthorizedError />}>
      <CoRetrieveDocumentId>
        {(coObject) =>
          coObject?.documentId ? (
            <AllCoObjectsSelector documentId={coObject.documentId} filterPartsNotOnDB={true}>
              {(coObjectsArray) => (
                <ObjectsListUpdatesPanel
                  documentId={coObject.documentId}
                  documentCoObjects={coObjectsArray.filter(
                    (coObject) => coObject.info.objectType === "DOCUMENT_PART_INCLUDE"
                  )}
                  updateSelectionOnly={true}
                  showObjectIcons={false}
                  mainHeaderTitle={formatMessage("cof.documentpartupdate.info")}
                  footerComponents={FooterChangesLaunchButton}
                  page={"updates"}
                  key={coObject.documentId}
                />
              )}
            </AllCoObjectsSelector>
          ) : (
            <DocumentNotValidError />
          )
        }
      </CoRetrieveDocumentId>
    </ProtectedComponent>
  );
};

interface FooterState {
  teachingBubbleVisible: boolean;
  modalVisible: boolean;
  selectedUpdateOption: IContextualMenuItem;
  objectUpdateOption: "DATAFULL" | "DATALESS";
  tableUpdateStrategy: TableUpdateStrategy;
}

export const FooterChangesLaunchButton = (props: {
  selectedObjects: Map<string, CoObjectItem>;
  username: string;
  isNotAuthorized: boolean;
}) => {
  const { selectedObjects, username, isNotAuthorized } = props;
  const getChangesLabel = formatMessage("cof.documentpartupdate.teachingbubble.getchanges.title");
  const getChangesAndUpdateLabel = formatMessage("cof.documentpartupdate.teachingbubble.getchangesfromlowest.title");

  const updateOptions: IContextualMenuItem[] = [
    {
      id: "get_changes_menuitem",
      key: "getChanges",
      text: getChangesLabel,
      depthLevel: "SHALLOWEST",
    },
    {
      id: "get_changes_and_update_menuitem",
      key: "getChangesFromLowestLevel",
      text: getChangesAndUpdateLabel,
      depthLevel: "DEEPEST",
    },
  ];

  const [state, setState] = useState<FooterState>({
    teachingBubbleVisible: false,
    modalVisible: false,
    selectedUpdateOption: updateOptions[0],
    objectUpdateOption: "DATALESS",
    tableUpdateStrategy: {
      overrideData: false,
      overrideStyle: false,
      typeUpdate: "update",
    },
  });

  useEffect(() => {
    // Resetta l'opzione di aggiornamento a "update" quando entrambi i toggle sono a false
    if (!state.tableUpdateStrategy.overrideStyle && !state.tableUpdateStrategy.overrideData)
      setState((s) => ({
        ...state,
        tableUpdateStrategy: {
          ...s.tableUpdateStrategy,
          typeUpdate: "update",
        },
      }));
  }, [state.tableUpdateStrategy.overrideStyle, state.tableUpdateStrategy.overrideData]);

  const toggleModal = () => setState({ ...state, modalVisible: !state.modalVisible });

  const setSelectedUpdateOption = (
    _?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    clickedItem?: IContextualMenuItem
  ) => setState({ ...state, selectedUpdateOption: clickedItem });

  const infoId = "UpdatesPageInfoId";

  const disabled = selectedObjects.size == 0 || isNotAuthorized;
  return (
    <>
      {state.modalVisible && (
        <FullPageModal
          modalProps={{ isOpen: state.modalVisible }}
          onClose={() => setState({ ...state, modalVisible: false })}
          textTitle={formatMessage("cof.documentpartupdate.getchangesandupdate.updateoptions")}
        >
          <div
            style={{
              fontWeight: "500",
              padding: "2px 10px",
              fontSize: "1.05rem",
              color: "var(--color-grey)",
            }}
          >
            <div
              dangerouslySetInnerHTML={{ __html: formatMessage("cof.updates.getchangesandupdate.updateoptions.info") }}
            />
          </div>
          <Stack horizontal style={{ padding: "8px 10px" }}>
            <span style={{ height: "100%", textAlign: "start", flexGrow: 1, float: "left" }}>
              {formatMessage("cof.documentpartupdate.getchangesandupdate.option.includedataupdate")}
            </span>
            <Toggle
              id="include_data_toggle"
              checked={state.objectUpdateOption === "DATAFULL"}
              onChange={() =>
                setState((s) => ({
                  ...s,
                  objectUpdateOption: s.objectUpdateOption === "DATAFULL" ? "DATALESS" : "DATAFULL",
                  tableUpdateStrategy: {
                    ...s.tableUpdateStrategy,
                    overrideData: false,
                    overrideStyle: false,
                  },
                }))
              }
              style={{ float: "right" }}
              styles={{ root: { marginBottom: 0 } }}
            />
          </Stack>
          <Stack horizontal style={{ padding: "8px 10px" }}>
            <span style={{ height: "100%", textAlign: "start", flexGrow: 1, float: "left" }}>
              {formatMessage("cof.documentpartupdate.getchangesandupdate.option.enablefullobjectreset")}
            </span>
            <Toggle
              id="full_reset_toggle"
              disabled={state.objectUpdateOption === "DATALESS"}
              checked={state.tableUpdateStrategy.overrideData}
              onChange={(_, opt) => {
                setState((s) => ({
                  ...state,
                  tableUpdateStrategy: {
                    ...s.tableUpdateStrategy,
                    typeUpdate: opt === true ? "fullReset" : s.tableUpdateStrategy.typeUpdate,
                    overrideData: !s.tableUpdateStrategy.overrideData,
                    overrideStyle: s.tableUpdateStrategy.overrideStyle ? s.tableUpdateStrategy.overrideData : false,
                  },
                }));
              }}
              style={{ float: "right" }}
              styles={{ root: { marginBottom: 0 } }}
            />
          </Stack>
          <Stack horizontal style={{ padding: "8px 10px" }}>
            <span style={{ height: "100%", textAlign: "start", flexGrow: 1, float: "left" }}>
              {formatMessage("cof.documentpartupdate.getchangesandupdate.option.keepalloverrides")}
            </span>
            <Toggle
              id="keep_all_override_toggle"
              disabled={state.objectUpdateOption === "DATALESS"}
              checked={state.tableUpdateStrategy.overrideStyle}
              onChange={(_, opt) => {
                setState((s) => ({
                  ...state,
                  tableUpdateStrategy: {
                    ...s.tableUpdateStrategy,
                    typeUpdate: opt == true ? "keepAllChanges" : s.tableUpdateStrategy.typeUpdate,
                    overrideStyle: !s.tableUpdateStrategy.overrideStyle,
                    overrideData: s.tableUpdateStrategy.overrideData ? s.tableUpdateStrategy.overrideStyle : false,
                  },
                }));
              }}
              style={{ float: "right" }}
              styles={{ root: { marginBottom: 0 } }}
            />
          </Stack>
          <div style={{ padding: "10px" }}>
            <FooterPanel>
              <PrimaryButton
                text={formatMessage("cof.documentpartupdate.button.update")}
                styles={{
                  label: {
                    whiteSpace: "nowrap",
                  },
                  root: { float: "right" },
                }}
                onClick={() => {
                  const updateOptions: UpdateOptions = {
                    depthLevel: state.selectedUpdateOption.depthLevel,
                    objectUpdateOption: state.objectUpdateOption,
                  };
                  const coObjects = Array.from(selectedObjects.values()).map((coObj) => ({
                    ...coObj,
                  }));
                  launchRefreshElaboration(username, coObjects, updateOptions, state.tableUpdateStrategy.typeUpdate);
                }}
              />
            </FooterPanel>
          </div>
        </FullPageModal>
      )}
      {state.teachingBubbleVisible && (
        <TeachingBubble
          calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
          target={`#${infoId}`}
          hasCloseButton={true}
          closeButtonAriaLabel="Close"
          onDismiss={() => setState({ ...state, teachingBubbleVisible: false })}
          headline={formatMessage("cof.documentpartupdate.teachingbubble.headline")}
        >
          <ul>
            <li>
              <b>{formatMessage("cof.documentpartupdate.teachingbubble.getchanges.title")}</b>:
              {formatMessage("cof.documentpartupdate.teachingbubble.getchanges.description")}
            </li>
            <li>
              <b>{formatMessage("cof.documentpartupdate.teachingbubble.getchangesfromlowest.title")}</b>:
              {formatMessage("cof.documentpartupdate.teachingbubble.getchangesfromlowest.description")}
            </li>
          </ul>
        </TeachingBubble>
      )}
      <div style={{ display: "flex", alignContent: "center", gap: "8px", float: "right" }}>
        <IconButton
          id={infoId}
          aria-label="Info"
          iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
          disabled={disabled}
          onClick={() => setState({ ...state, teachingBubbleVisible: !state.teachingBubbleVisible })}
        />
        <PrimaryButton
          id="get_changes_button"
          className={ButtonVariants.primary}
          text={state.selectedUpdateOption.text}
          styles={{
            label: {
              whiteSpace: "nowrap",
            },
            root: { float: "right" },
          }}
          disabled={disabled}
          split
          splitButtonMenuProps={{ id: "get_changes_split_button" }}
          menuProps={{
            items: updateOptions,
            isBeakVisible: false,
            onItemClick: setSelectedUpdateOption,
          }}
          onClick={() => {
            const updateOptions: UpdateOptions = {
              depthLevel: state.selectedUpdateOption.depthLevel,
            };
            state.selectedUpdateOption.depthLevel === "SHALLOWEST"
              ? launchRefreshElaboration(username, Array.from(selectedObjects.values()), updateOptions)
              : toggleModal();
          }}
        />
      </div>
    </>
  );
};
